import React, { useMemo, useState } from 'react';
import { Category } from '../types';
import { usePos } from '../context/PosContext';
import './Products.css';

// Category Master (add_category_dialog_fragment.xml / category_view_layout.xml):
// manages the product category list (name, regional name, serial order). The
// names feed the Product Master category picker.
const blank = { name: '', regionalName: '', serialNo: '', discountPercent: '', parentCategory: '' };

const Categories: React.FC = () => {
  const { categories, products, addCategory, updateCategory, deleteCategory } = usePos();
  const [editing, setEditing] = useState<Category | null>(null);
  const [listSearch, setListSearch] = useState('');
  const [f, setF] = useState({ ...blank });

  const set = (k: keyof typeof blank, v: string) => setF((prev) => ({ ...prev, [k]: v }));

  // Product count per category name (a top-level category is referenced by a
  // product's `category`, a sub-category by its `subCategory`).
  const countFor = (name: string) =>
    products.filter((p) => p.category === name || p.subCategory === name).length;

  // Top-level categories (no parent) — the candidates a category can be nested
  // under. A category cannot be its own parent (single-level nesting only).
  const parentOptions = useMemo(
    () =>
      categories
        .filter((c) => !c.parentCategory && c.id !== editing?.id)
        .sort((a, b) => (a.serialNo ?? 9999) - (b.serialNo ?? 9999) || a.name.localeCompare(b.name))
        .map((c) => c.name),
    [categories, editing]
  );

  // Category names used on products but not yet in the master, so they can be imported.
  const orphanNames = useMemo(() => {
    const known = new Set(categories.map((c) => c.name));
    const out = new Set<string>();
    products.forEach((p) => p.category && !known.has(p.category) && out.add(p.category));
    return Array.from(out);
  }, [categories, products]);

  const sorted = useMemo(() => {
    const t = listSearch.trim().toLowerCase();
    return [...categories]
      .filter((c) => !t || c.name.toLowerCase().includes(t) || c.regionalName?.toLowerCase().includes(t))
      .sort((a, b) => (a.serialNo ?? 9999) - (b.serialNo ?? 9999) || a.name.localeCompare(b.name));
  }, [categories, listSearch]);

  const reset = () => {
    setF({ ...blank });
    setEditing(null);
  };

  const buildData = (): Omit<Category, 'id'> => ({
    name: f.name.trim(),
    regionalName: f.regionalName.trim() || undefined,
    serialNo: f.serialNo ? parseInt(f.serialNo, 10) : undefined,
    discountPercent: f.discountPercent ? parseFloat(f.discountPercent) || undefined : undefined,
    parentCategory: f.parentCategory.trim() || undefined,
  });

  const save = () => {
    const name = f.name.trim();
    if (!name) {
      alert('Category name is required.');
      return;
    }
    const dup = categories.find(
      (c) => c.name.toLowerCase() === name.toLowerCase() && c.id !== editing?.id
    );
    if (dup) {
      alert('A category with that name already exists.');
      return;
    }
    // One level of nesting only: a category that is itself a parent can't be
    // turned into a sub-category.
    if (f.parentCategory.trim() && categories.some((c) => c.parentCategory === name)) {
      alert(`"${name}" already has sub-categories, so it can't become a sub-category itself.`);
      return;
    }
    if (editing) updateCategory({ ...editing, ...buildData() });
    else addCategory(buildData());
    reset();
  };

  const edit = (c: Category) => {
    setEditing(c);
    setF({
      name: c.name,
      regionalName: c.regionalName ?? '',
      serialNo: c.serialNo != null ? String(c.serialNo) : '',
      discountPercent: c.discountPercent != null ? String(c.discountPercent) : '',
      parentCategory: c.parentCategory ?? '',
    });
  };

  const remove = () => {
    if (!editing) return;
    const inUse = countFor(editing.name);
    const msg = inUse
      ? `Delete "${editing.name}"? ${inUse} product(s) use it — they keep the name but it leaves the master list.`
      : 'Delete this category?';
    if (window.confirm(msg)) {
      deleteCategory(editing.id);
      reset();
    }
  };

  const importOrphan = (name: string) =>
    addCategory({ name, serialNo: categories.length + 1 });

  const lbl = (t: string) => <label className="pf-lbl">{t}</label>;

  return (
    <div className="pm-page">
      {/* list */}
      <div className="pm-list card-rounded">
        <input
          className="pm-list-search"
          placeholder="Search Category"
          value={listSearch}
          onChange={(e) => setListSearch(e.target.value)}
        />
        <div className="pm-list-scroll">
          {sorted.map((c) => (
            <button key={c.id} className={`pm-list-item ${editing?.id === c.id ? 'sel' : ''}`} onClick={() => edit(c)}>
              <span className="pm-li-name">
                {c.parentCategory ? '↳ ' : ''}
                {c.serialNo != null ? `${c.serialNo}. ` : ''}
                {c.name}
              </span>
              <span className="pm-li-meta">
                {c.parentCategory ? `in ${c.parentCategory} · ` : ''}
                {c.regionalName ? `${c.regionalName} · ` : ''}
                {countFor(c.name)} product{countFor(c.name) === 1 ? '' : 's'}
              </span>
            </button>
          ))}
          {sorted.length === 0 && (
            <p className="muted" style={{ padding: 16 }}>
              No categories yet.
            </p>
          )}
        </div>
      </div>

      {/* form */}
      <div className="pm-form card-rounded">
        <div className="pf-top">
          <span style={{ fontWeight: 700, color: 'var(--color-primary-dark)' }}>
            {editing ? 'Edit Category' : 'New Category'}
          </span>
          <div className="pf-top-btns">
            {editing && (
              <button className="pf-btn-orange" onClick={reset}>
                New
              </button>
            )}
            {editing && (
              <button className="pf-btn-orange" onClick={remove}>
                Delete
              </button>
            )}
            <button className="pf-btn-orange" onClick={save}>
              {editing ? 'Update' : 'Save'}
            </button>
          </div>
        </div>

        <div className="pf-scroll">
          <div className="pf-row r1">
            <div>{lbl('Category *')}<input value={f.name} onChange={(e) => set('name', e.target.value)} /></div>
          </div>
          <div className="pf-row r2">
            <div>{lbl('Regional Name')}<input value={f.regionalName} onChange={(e) => set('regionalName', e.target.value)} /></div>
            <div>{lbl('Serial No')}<input type="number" value={f.serialNo} onChange={(e) => set('serialNo', e.target.value)} /></div>
          </div>
          <div className="pf-row r2">
            <div>
              {lbl('Discount % (Category Wise Discount)')}
              <input type="number" value={f.discountPercent} onChange={(e) => set('discountPercent', e.target.value)} placeholder="0" />
            </div>
            <div>
              {lbl('Parent Category (makes this a sub-category)')}
              <select value={f.parentCategory} onChange={(e) => set('parentCategory', e.target.value)}>
                <option value="">— None (top-level) —</option>
                {/* keep a legacy parent selectable even if it's gone from the master */}
                {f.parentCategory && !parentOptions.includes(f.parentCategory) && (
                  <option value={f.parentCategory}>{f.parentCategory}</option>
                )}
                {parentOptions.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="muted" style={{ fontSize: 12 }}>
            Applied per line on the Bill screen when App Setting → Advance → General →
            "D - Category Wise Discount" is on (falls back to the product's own discount).
          </p>

          {orphanNames.length > 0 && (
            <>
              <div className="pf-section">Used on products, not in master</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, padding: '4px 0' }}>
                {orphanNames.map((name) => (
                  <button key={name} className="pf-btn-orange" onClick={() => importOrphan(name)} title="Add to category master">
                    + {name}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Categories;
